/**
 * /salience/methods — the "room to breathe" explainer for the Salience Monitor.
 * Written for people who don't know the academic work: what the monitor is, the
 * four data sources in depth (what each pulls, from where, and why), the three
 * separate coded dimensions, a plain-English "research behind this" list, and an
 * honest prototype note. The main pages carry short callouts; this is the full
 * story they link to.
 *
 * Light reading chrome (not the terminal register). House style: no em-dashes.
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PrototypeNote } from "../components/salience/explainers";

export function SalienceMethods() {
  return (
    <div className="mx-auto max-w-3xl pb-16">
      <header className="mb-8">
        <Link
          to="/salience"
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-ink"
        >
          <ArrowLeft size={14} /> Salience monitor
        </Link>
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Salience monitor · Methods &amp; sources
        </div>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink">
          How this works, and why you can trust it (a bit)
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-ink-muted">
          A plain-English tour of what the Salience Monitor measures, where the
          data comes from, and the research the approach is built on. No prior
          knowledge assumed.
        </p>
      </header>

      {/* What this monitors */}
      <Section title="What this monitors">
        <p>
          The Salience Monitor watches how much attention the idea of a{" "}
          <strong className="text-ink">wealth tax</strong> is getting in the wider
          world, and how far Gary's way of framing it travels. Where the YouTube
          side of the portal looks inward at Gary's own audience, this looks
          outward: is the message reaching, spreading, and landing, and can we
          trace Gary's specific framing into the media and political arena?
        </p>
        <p>
          One thing to be clear about up front: we measure{" "}
          <strong className="text-ink">attention</strong> (how prominent the topic
          is, how often it shows up), not <em>importance</em> (how much people
          actually care). Those are different things, and treating them as the
          same is a well-documented mistake, so we stick to prominence over time.
        </p>
      </Section>

      {/* What we actually track */}
      <Section title="What we actually track">
        <p>
          "Wealth tax" is the core, but it is not the whole watchlist. We follow a
          handful of related <strong className="text-ink">topics</strong> (wealth
          tax, tax the rich, billionaire tax), Gary's{" "}
          <strong className="text-ink">tagline</strong> ("tax wealth, not work"),
          and around two dozen <strong className="text-ink">people and
          organisations</strong> in and around the movement: allied economists
          (such as Piketty, Zucman, and Richard Murphy), advocacy groups (such as
          Patriotic Millionaires and Tax Justice UK), adjacent creators, and
          wealth-tax-sympathetic politicians.
        </p>
        <p>
          There is an important catch in how the people and groups are counted.
          Most of them are <strong className="text-ink">topic-scoped</strong>:
          they only register when they appear alongside the wealth-tax
          conversation, so the number reflects how often they come up{" "}
          <em>in that context</em>, a loose economics co-signal, rather than their
          overall fame or activity. A few (Gary himself, his book, and the main
          advocacy groups) are tracked in full. On a person's or group's own page,
          topic-scoped terms are labelled as such.
        </p>
        <p>
          One honest caveat: this set of topics and names was chosen from some
          early desk research plus the audience's own collaboration suggestions.
          It is a reasonable starting point, not a validated or complete list.
          Some entries may turn out to be off-target, and useful ones may be
          missing. Landing on the right, most helpful mix would take more
          collaboration with Gary and the team.
        </p>
      </Section>

      {/* The four conversations */}
      <Section title="The four conversations we track">
        <p>
          We watch four separate conversations, and we keep them apart on
          purpose. A topic can be loud in the news yet quiet in Parliament, and
          that gap is itself part of the story. We never blend them into a single
          "salience score", because that would hide exactly where they agree and
          where they pull apart.
        </p>
        <SourceCard
          accent="var(--color-agenda-public)"
          name="Public"
          source="YouTube video volume"
        >
          How many videos creators post about the topic: a search across YouTube,
          counted per day, with Gary's own uploads separated out so we can tell
          the movement apart from Gary himself. The counts are approximate
          (YouTube search is loose), so a language model checks each hit for
          relevance.
        </SourceCard>
        <SourceCard
          accent="var(--color-agenda-search)"
          name="Search"
          source="Google Trends"
        >
          How much people look the topic up, via Google Trends. The figure is an
          index from 0 to 100 (100 is that term's own busiest point in the
          window), not a percentage, so it shows the shape of interest over time
          rather than a headcount. This one applies to the topic terms only, and
          for the demo we have search data for at least the wealth-tax topic;
          other terms may show nothing yet.
        </SourceCard>
        <SourceCard
          accent="var(--color-agenda-media)"
          name="Media"
          source="GDELT global news index"
        >
          GDELT is an open, free index of the world's online news. Rather than
          counting raw articles (which just tracks how busy the news week was), we
          use its coverage <em>share</em>: what fraction of all global coverage
          mentions the topic. That way a quiet news week and a busy one are
          comparable. GDELT also tags articles by country, but those tags are
          rough, so we read the UK-versus-global balance rather than exact figures.
        </SourceCard>
        <SourceCard
          accent="var(--color-agenda-elite)"
          name="Elite"
          source="UK Parliament & government"
        >
          How often the topic surfaces in official business: Hansard (the record
          of Commons and Lords debates), Early Day Motions, Written Questions,
          public petitions, and GOV.UK. Parliamentary attention is a long-standing
          way to measure what government is actually engaging with. A language
          model filters out loose keyword matches (for example a question about
          "wealth" that has nothing to do with a wealth tax).
        </SourceCard>
      </Section>

      {/* Three separate coded dimensions */}
      <Section title="Stance, frame, and tone are three separate things">
        <p>
          When we read individual items, we code three things separately, because
          they answer different questions:
        </p>
        <ul className="mt-2 space-y-2">
          <li>
            <strong className="text-ink">Stance</strong> is whether the text
            argues for or against a wealth tax. This is a position on the
            proposal, not the mood of the language. An angry comment can still
            support the policy.
          </li>
          <li>
            <strong className="text-ink">Frame</strong> is the angle the argument
            takes: fairness, the risk of wealth leaving the country, whether it is
            workable, or "tax the billionaires". Our automatic frame tagging only
            agrees with human coders about six times in ten, so we treat it as a
            rough steer, not a precise figure.
          </li>
          <li>
            <strong className="text-ink">Tone</strong> is a rough positive or
            negative reading of the language. Automatic tone is unreliable, so we
            show it quietly and would not lean on it.
          </li>
        </ul>
      </Section>

      {/* Candidate influence */}
      <Section title="“Candidate influence”, and why it is only a candidate">
        <p>
          One thing we look for is whether attention rising in one conversation
          tends to come shortly before a rise in another (for example, public
          chatter leading media coverage). That is a hint one might be leading the
          other. But a statistical "came before" is not proof of cause, so we only
          ever label these as <strong className="text-ink">candidates</strong>,
          and we check each one against a calendar of big events (Budgets,
          elections, major reports) that could easily explain both rises at once.
        </p>
      </Section>

      {/* The research behind this */}
      <Section title="The research behind this">
        <p className="mb-3">
          The approach borrows from established work in political and communication
          science. In plain terms, here is what each idea is and how we use it.
        </p>
        <div className="space-y-4">
          <Paper cite="McCombs & Shaw (1972); Rogers & Dearing (1988)" name="Agenda-setting">
            The finding that the media, the public, and policymakers each have
            their own "agenda" of what is getting attention, and that these
            influence one another. We use it as the backbone: each conversation
            is tracked separately and never merged into one number.
          </Paper>
          <Paper cite="Wlezien (2005)" name="Attention is not importance">
            Work showing that how much attention an issue gets is a different
            thing from how important people say it is. This is why our copy always
            says "attention" and never claims to measure how much people care.
          </Paper>
          <Paper cite="Bestvater & Monroe (2023)" name="Stance is not sentiment">
            Research demonstrating that a text's position on an issue (for or
            against) is not the same as whether its language is positive or
            negative, and that the two should not be swapped. We code stance and
            tone as separate fields for exactly this reason.
          </Paper>
          <Paper cite="Entman (1993)" name="Framing">
            The idea that the same issue can be presented through competing
            "frames" that each emphasise a different problem. Our frame breakdown
            (fairness, capital flight, feasibility, billionaires) comes straight
            from this.
          </Paper>
          <Paper cite="Granger (2004); Kingdon (policy windows)" name="Lead-lag precedence">
            A statistical test for whether one time series reliably precedes
            another. Granger himself stressed it shows precedence, not causation,
            so we present results as candidates and cross-check them against the
            events calendar (Kingdon's idea that big moments open "policy windows").
          </Paper>
          <Paper cite="Krippendorff (2018)" name="Coder reliability">
            The standard for checking whether an automated coder agrees with a
            human, reported as a number called Krippendorff's alpha. We run this
            check and publish it, so the weaker dimensions (like frame) are flagged
            honestly.{" "}
            <Link
              to="/salience/eval"
              className="font-medium text-brand-indigo hover:underline"
            >
              See the reliability check →
            </Link>
          </Paper>
        </div>
      </Section>

      {/* Prototype honesty */}
      <Section title="This is a prototype">
        <PrototypeNote tone="light" variant="full" evalLink />
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <h2 className="mb-3 text-lg font-bold tracking-tight text-ink">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-ink-muted">
        {children}
      </div>
    </section>
  );
}

function SourceCard({
  accent,
  name,
  source,
  children,
}: {
  accent: string;
  name: string;
  source: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-rule bg-surface p-4">
      <div className="mb-1.5 flex items-center gap-2">
        <span
          className="inline-block h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: accent }}
          aria-hidden
        />
        <span className="text-sm font-bold text-ink">{name}</span>
        <span className="text-xs text-ink-muted">· {source}</span>
      </div>
      <p className="text-sm leading-relaxed text-ink-muted">{children}</p>
    </div>
  );
}

function Paper({
  name,
  cite,
  children,
}: {
  name: string;
  cite: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-l-2 border-brand-indigo/30 pl-3">
      <div className="text-sm font-semibold text-ink">{name}</div>
      <p className="mt-0.5 text-sm leading-relaxed text-ink-muted">{children}</p>
      <div className="mt-1 text-[11px] italic text-ink-muted/70">{cite}</div>
    </div>
  );
}
